import { Outlet, Link, useLocation } from 'react-router-dom'
import { DemoControls } from '@/ux/demo'
import { useContent } from '@/lib/content-context'
import { useUX } from '@/ux'
import { AssistantButton } from './Assistant'
import { ThemeToggle } from './ThemeToggle'
import { LanguageToggle } from './LanguageToggle'
import { useTranslation } from '@/i18n'

export function Layout() {
  const { links } = useContent()
  const { persona, setPersona } = useUX()
  const { t } = useTranslation()
  const location = useLocation()

  const isActive = (path: string) => location.pathname === path

  return (
    <div className="min-h-screen flex flex-col">
      <DemoControls />
      <AssistantButton />

      {/* Header/Navigation */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm dark:bg-gray-900 dark:border-gray-700">
        <nav className="max-w-6xl mx-auto px-4 py-3 md:py-4" aria-label="Main navigation">
          <div className="flex items-center justify-between">
            {/* Logo/Home link */}
            <Link
              to="/"
              className="text-lg md:text-xl font-bold hover:text-blue-600 transition dark:hover:text-blue-400"
              aria-label="Home"
            >
              DevOps → Data
            </Link>

            {/* Nav links */}
            <div className="flex items-center gap-2 md:gap-6" role="navigation">
              <Link
                to="/"
                className={`text-sm md:text-base hover:text-blue-600 dark:hover:text-blue-400 transition ${
                  isActive('/')
                    ? 'text-blue-600 dark:text-blue-400 font-semibold'
                    : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                {t('nav.home')}
              </Link>
              <Link
                to="/projects"
                className={`text-sm md:text-base hover:text-blue-600 dark:hover:text-blue-400 transition ${
                  isActive('/projects')
                    ? 'text-blue-600 dark:text-blue-400 font-semibold'
                    : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                {t('nav.projects')}
              </Link>
              <Link
                to="/about"
                className={`text-sm md:text-base hover:text-blue-600 dark:hover:text-blue-400 transition ${
                  isActive('/about')
                    ? 'text-blue-600 dark:text-blue-400 font-semibold'
                    : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                {t('nav.about')}
              </Link>

              {/* Language toggle - Hidden on mobile */}
              <div className="hidden md:block">
                <LanguageToggle />
              </div>

              {/* Theme toggle - Hidden on mobile */}
              <div className="hidden md:block">
                <ThemeToggle />
              </div>

              {/* Persona toggle - Desktop only */}
              <div className="hidden md:flex gap-2" role="group" aria-label="View switcher">
                <button
                  type="button"
                  onClick={() => setPersona('recruiter')}
                  className={`px-3 py-1 text-sm rounded ${
                    persona === 'recruiter'
                      ? 'bg-blue-600 text-white dark:bg-blue-500'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-300'
                  }`}
                  aria-label="Switch to recruiter view"
                  aria-pressed={persona === 'recruiter'}
                  title={t('persona.recruiter')}
                >
                  👔
                </button>
                <button
                  type="button"
                  onClick={() => setPersona('engineer')}
                  className={`px-3 py-1 text-sm rounded ${
                    persona === 'engineer'
                      ? 'bg-blue-600 text-white dark:bg-blue-500'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-300'
                  }`}
                  aria-label="Switch to engineer view"
                  aria-pressed={persona === 'engineer'}
                  title={t('persona.engineer')}
                >
                  💻
                </button>
                <Link
                  to="/game"
                  className="px-3 py-1 text-sm rounded bg-purple-600 hover:bg-purple-700 text-white transition-colors"
                  aria-label="Switch to gamer view"
                  title="Gamer Persona"
                >
                  🎮
                </Link>
              </div>
            </div>
          </div>

          {/* Persona toggle - Mobile only (below header) */}
          <div
            className="flex items-center justify-center gap-2 mt-3 md:hidden"
            role="group"
            aria-label="View switcher"
          >
            <button
              type="button"
              onClick={() => setPersona('recruiter')}
              className={`px-2 py-1 text-xs rounded ${
                persona === 'recruiter'
                  ? 'bg-blue-600 text-white dark:bg-blue-500'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-300'
              }`}
              aria-label="Switch to recruiter view"
              aria-pressed={persona === 'recruiter'}
              title={t('persona.recruiter')}
            >
              👔
            </button>
            <button
              type="button"
              onClick={() => setPersona('engineer')}
              className={`px-2 py-1 text-xs rounded ${
                persona === 'engineer'
                  ? 'bg-blue-600 text-white dark:bg-blue-500'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-300'
              }`}
              aria-label="Switch to engineer view"
              aria-pressed={persona === 'engineer'}
              title={t('persona.engineer')}
            >
              💻
            </button>
            <Link
              to="/game"
              className="px-2 py-1 text-xs rounded bg-purple-600 hover:bg-purple-700 text-white transition-colors"
              aria-label="Switch to gamer view"
              title="Gamer Persona"
            >
              🎮
            </Link>
            <div className="ml-2">
              <LanguageToggle />
            </div>
            <ThemeToggle />
          </div>
        </nav>
      </header>

      {/* Main content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="py-8 bg-gray-900 text-white text-center text-sm dark:bg-gray-950">
        <div className="max-w-6xl mx-auto px-4">
          <p className="mb-2">
            © {new Date().getFullYear()} Portfolio. {t('footer.builtWith')}
          </p>
          <div className="flex gap-4 justify-center text-xs text-gray-400 dark:text-gray-500">
            <a
              href={links.github}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white"
            >
              GitHub
            </a>
            <a
              href={links.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white"
            >
              LinkedIn
            </a>
            <a href={`mailto:${links.email}`} className="hover:text-white">
              {t('footer.email')}
            </a>
          </div>
        </div>
      </footer>
    </div>
  )
}
